import { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MessageCircle, RefreshCw, Trash2, CheckCircle2, Bell } from "lucide-react";
import { toast } from "sonner";

const dayLabel = (d, t) => {
  if (d === 0) return t("dueToday");
  if (d > 0) return t("dueInDays").replace("{n}", d);
  return t("overdueDays").replace("{n}", Math.abs(d));
};

const statusStyle = {
  pending: "bg-amber-50 text-amber-700",
  sent: "bg-emerald-50 text-emerald-700",
};

export default function Reminders() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const { data } = await api.get("/reminders");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const scanNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reminders/scan");
      toast.success(`+${data.upserted} · ${data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const openWhatsApp = async (r) => {
    if (!r.wa_url) return toast.error(t("phone") + " ?");
    window.open(r.wa_url, "_blank");
    // optimistic mark sent
    try {
      await api.post(`/reminders/${r.id}/mark-sent`);
      load();
    } catch {}
  };

  const markSent = async (id) => {
    await api.post(`/reminders/${id}/mark-sent`);
    load();
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/reminders/${id}`);
    load();
  };

  const filtered = items.filter((r) => filter === "all" ? true : r.status === filter);

  const pending = items.filter((r) => r.status === "pending").length;
  const sent = items.filter((r) => r.status === "sent").length;

  return (
    <div className="space-y-6" data-testid="reminders-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label flex items-center gap-2">
            <Bell size={14} className="text-[hsl(var(--accent))]" />
            {t("paymentReminders")}
          </div>
          <h1 className="display-font text-3xl font-bold text-[hsl(var(--primary))] mt-1">
            {t("paymentReminders")}
          </h1>
          <div className="mt-3 divider-accent" />
        </div>
        {isAdmin && (
          <Button onClick={scanNow} disabled={busy} className="btn-accent" data-testid="reminders-scan-btn">
            <RefreshCw size={15} className={"me-2 " + (busy ? "animate-spin" : "")} />
            {t("scanNow")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="dashboard-card" data-testid="reminders-stat-pending">
          <div className="uppercase-label">{t("pending")}</div>
          <div className="stat-value mt-1 text-amber-600">{pending}</div>
        </div>
        <div className="dashboard-card" data-testid="reminders-stat-sent">
          <div className="uppercase-label">{t("sentLabel")}</div>
          <div className="stat-value mt-1 text-emerald-600">{sent}</div>
        </div>
        <div className="dashboard-card">
          <div className="uppercase-label">{t("filter")}</div>
          <div className="mt-2 flex gap-1">
            {["all", "pending", "sent"].map((k) => (
              <Button key={k} size="sm" variant={filter === k ? "default" : "outline"}
                onClick={() => setFilter(k)}
                className={filter === k ? "bg-[hsl(var(--primary))] text-white" : ""}
                data-testid={`reminders-filter-${k}`}>
                {t(k === "all" ? "all" : k === "sent" ? "sentLabel" : "pending")}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="dashboard-card">
        {filtered.length === 0 ? (
          <div className="text-center py-14 text-sm text-[hsl(var(--muted-foreground))]">
            <Bell className="mx-auto mb-3 text-[hsl(var(--muted-foreground))]" size={30} />
            {t("emptyReminders")}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("client")}</TableHead>
                <TableHead>{t("refNo")}</TableHead>
                <TableHead>{t("program")}</TableHead>
                <TableHead>{t("startDate")}</TableHead>
                <TableHead>{t("balanceDue")}</TableHead>
                <TableHead>{t("timing")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead className="text-end">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`reminder-row-${r.id}`}>
                  <TableCell className="font-semibold">{r.client_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.booking_ref}</TableCell>
                  <TableCell>{r.program_name || "—"}</TableCell>
                  <TableCell>{r.start_date}</TableCell>
                  <TableCell className="font-semibold text-rose-600">
                    {(r.balance || 0).toLocaleString()} {r.currency}
                  </TableCell>
                  <TableCell>
                    <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs " +
                      (r.trigger_day < 0 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-700")}>
                      {dayLabel(r.trigger_day, t)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs " + (statusStyle[r.status] || statusStyle.pending)}>
                      {t(r.status === "sent" ? "sentLabel" : "pending")}
                    </span>
                  </TableCell>
                  <TableCell className="text-end">
                    <div className="inline-flex items-center gap-1 rtl:flex-row-reverse">
                      {r.status !== "sent" && r.wa_url && (
                        <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" onClick={() => openWhatsApp(r)} data-testid={`reminder-wa-${r.id}`}>
                          <MessageCircle size={13} className="me-1" /> WhatsApp
                        </Button>
                      )}
                      {r.status !== "sent" && (
                        <Button size="icon" variant="ghost" onClick={() => markSent(r.id)} className="text-emerald-700" data-testid={`reminder-mark-${r.id}`}>
                          <CheckCircle2 size={15} />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => del(r.id)} className="text-red-600" data-testid={`reminder-del-${r.id}`}>
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
